import * as React from 'react';
import { useUserInfo } from '../../../context/UserInfo';
import AvatarWithActionsMenu from '../../../../../atomic/molecules/AvatarWithActionsMenu';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useLogout } from '../../../../../hooks/useLogout';
import { useHistory } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { routes } from '../../../routes';
import { EmployeeAvatarInputModalConnected } from '../../EmployeeAvatarInput/EmployeeAvatarInputModalConnected';
import './TopNavAvatarWithActions.css';
import { LEARNING_HUB_URL } from '../../../../../constants/hubUrls';
import { DropdownActionItem } from '../../../../../atomic/molecules/DropdownActionItem';
import { useIsScreenSizeLargerThanOrEqual } from '../../../../../hooks/useIsScreenSizeLargerThan';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import {
  HubElementContentType,
  useHubElements,
} from '../../../hooks/useHubElements';
import { useHubElementHandler } from '../../../hooks/useHubElementHandler';
import { useAvatarPermissions } from '../../EmployeeAvatarInput/useAvatarPermissions';

const i18Path = 'dashboard.dropDownActions';

const TopNavAvatarWithActions: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const employee = useUserInfo();
  const t = useNamespacedTranslation(i18Path);
  const logout = useLogout();
  const history = useHistory();
  const isLargeScreen = useIsScreenSizeLargerThanOrEqual('lg');

  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
  const avatarPermissions = useAvatarPermissions();

  const handleRedirectToLearningHub = () => {
    window.open(LEARNING_HUB_URL);
  };

  const onSettingClick = useCallback(
    () => history.push(routes.general.route.create({})),
    [history],
  );

  const { profileDropdown, getGroupChildren } = useHubElements();
  const onActivate = useHubElementHandler();

  const renderItem = (
    element: (typeof profileDropdown)[number],
    nested: boolean,
  ) => (
    <DropdownActionItem key={element.Id} onClick={() => onActivate(element)}>
      <span
        className={`d-flex align-items-center gap-2${nested ? ' ps-3' : ''}`}>
        {element.flair__Menu_Item_Name__c || element.Name}
        {element.config.contentType === HubElementContentType.ExternalUrl && (
          <FlairIcon icon="external-link-outline" size="sm" />
        )}
      </span>
    </DropdownActionItem>
  );

  const hubElementActions = profileDropdown.flatMap((element) => {
    if (element.config.contentType === HubElementContentType.Group) {
      const children = getGroupChildren(element.Id).filter(
        (c) => c.config.contentType !== HubElementContentType.Group,
      );
      return [
        <DropdownActionItem
          key={`${element.Id}-header`}
          disabled
          className="text-uppercase small text-muted">
          {element.flair__Menu_Item_Name__c || element.Name}
        </DropdownActionItem>,
        ...children.map((child) => renderItem(child, true)),
      ];
    }
    return [renderItem(element, false)];
  });

  const actions = [
    avatarPermissions.canOpenModal && (
      <DropdownActionItem
        key="changeAvatarAction"
        onClick={() => setUploadModalVisible(true)}>
        {t('changeAvatar')}
      </DropdownActionItem>
    ),
    <DropdownActionItem key="settingsAction" onClick={onSettingClick}>
      {t('settings')}
    </DropdownActionItem>,
    <DropdownActionItem
      key="learningHubAction"
      onClick={handleRedirectToLearningHub}>
      {t('learningHub')}
    </DropdownActionItem>,
    ...hubElementActions,
    <DropdownActionItem key="logoutAction" onClick={logout}>
      {t('logout')}
    </DropdownActionItem>,
  ];

  return (
    <>
      <AvatarWithActionsMenu
        employee={employee}
        actions={actions}
        showAvatarOnly={!isLargeScreen}
        avatarClassName="top-nav-avatar"
      />
      <EmployeeAvatarInputModalConnected
        show={uploadModalVisible}
        canUpload={avatarPermissions.canUpload}
        canDelete={avatarPermissions.canDelete}
        onClose={() => setUploadModalVisible(false)}
      />
    </>
  );
};

export default TopNavAvatarWithActions;
